/**
 * Font-size units, converted through CSS pixels.
 *
 * The absolute units are fixed by CSS Values and Units Level 4, "Absolute lengths"
 * (w3.org/TR/css-values-4/#absolute-lengths): 1in = 2.54cm = 96px, 1pt = 1/72 in,
 * 1pc = 1/6 in, 1mm = 1/10 cm.
 * So 1pt = 4/3 px exactly, and the 12-point type of a word processor is 16 CSS pixels.
 *
 * The relative units need a reference size: `rem` is relative to the root element's font
 * size, while `em` and `%` (as font sizes) are relative to the parent element's. Browsers
 * default both to 16px, which is why the widget starts there and lets the reader change
 * either.
 */

export const PX_PER_INCH = 96;
export const PT_PER_INCH = 72;
export const MM_PER_INCH = 25.4;

export const FONT_UNITS = [
  "pt",
  "px",
  "em",
  "rem",
  "percent",
  "mm",
  "cm",
  "in",
  "pc",
] as const;

export type FontUnit = (typeof FONT_UNITS)[number];

export interface FontBases {
  /** The root element's font size in px (what 1rem equals). */
  root: number;
  /** The parent element's font size in px (what 1em and 100% equal). */
  parent: number;
}

export const DEFAULT_BASES: FontBases = { root: 16, parent: 16 };

/** How many CSS pixels one of each unit is. */
export function pxPerUnit(unit: FontUnit, bases: FontBases): number {
  switch (unit) {
    case "px":
      return 1;
    case "pt":
      return PX_PER_INCH / PT_PER_INCH;
    case "pc":
      return (PX_PER_INCH / PT_PER_INCH) * 12;
    case "in":
      return PX_PER_INCH;
    case "mm":
      return PX_PER_INCH / MM_PER_INCH;
    case "cm":
      return (PX_PER_INCH / MM_PER_INCH) * 10;
    case "em":
      return bases.parent;
    case "rem":
      return bases.root;
    case "percent":
      return bases.parent / 100;
    default:
      return 1;
  }
}

export function toPx(value: number, unit: FontUnit, bases: FontBases): number {
  return value * pxPerUnit(unit, bases);
}

export function fromPx(px: number, unit: FontUnit, bases: FontBases): number {
  return px / pxPerUnit(unit, bases);
}

/** One size in every unit. */
export function convertFontSize(
  value: number,
  unit: FontUnit,
  bases: FontBases
): Record<FontUnit, number> {
  const px = toPx(value, unit, bases);
  const result = {} as Record<FontUnit, number>;
  for (const each of FONT_UNITS) {
    result[each] = fromPx(px, each, bases);
  }
  return result;
}

/** The CSS spelling of a value: `1.5rem`, `150%`. Machine text — no locale grouping. */
export function cssValue(value: number, unit: FontUnit): string {
  const rounded = Number.parseFloat(value.toFixed(4));
  return unit === "percent" ? `${rounded}%` : `${rounded}${unit}`;
}

/** Point sizes for the reference table: the word-processor menu, plus display sizes. */
export const COMMON_POINT_SIZES = [
  6, 7, 8, 9, 10, 10.5, 11, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 48, 72,
] as const;
